// Standard Reports — a fixed-format reporting section under the CSV builder on the
// Reports page (built 2 Sep 2026). The user sets a filter, picks a report, and
// prints it as a PDF that opens in a new tab (letterhead + tables) — nothing is
// rendered on screen. Two reports:
//   Report 1 — Number of Shelters (per state, onboarded by From date vs To date)
//   Report 2 — Children Assisted by Category (per state: # children, $ cost, avg)
// The aggregation is pure + unit-tested in src/services/standardReports.js; the PDF
// layout is in src/utils/standardReportsPdf.ts. This file is the filter UI + the
// two data loads (services list + the customer-type → category map, reused from the
// CRM Audience "Customer type" filter).

import { useEffect, useMemo, useState } from "react";
import { loadCustomerTypeCategories } from "../../data/lookups";
import { listShelterProfiles } from "../../data/profiles";
import { LIST_LIMIT, listAllVisibleRequests } from "../../data/requests";
import { listResponderTasks } from "../../data/requestTasks";
import { listAllShelters } from "../../data/shelters";
import { listAllUserShelters } from "../../data/userShelters";
import { alertDialog } from "../../services/dialogService";
import { fetchLoginActivation } from "../../services/loginActivation";
import {
  childrenByCategory,
  requestsByState,
  STANDARD_CATEGORIES,
  shelterCountsByState,
  sheltersWithoutActivatedLogin,
  usersByState,
} from "../../services/standardReports";
import { buildStatisticsReport } from "../../services/statisticsReport";
import { averageResponseHours } from "../../utils/responseTime";
import { openStandardReportPdf } from "../../utils/standardReportsPdf";

const STATES = ["All", "QLD", "NSW", "VIC", "WA", "SA", "TAS", "NT", "ACT"];
const selectStyle =
  "py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";
const cardStyle = {
  background: "#FFFFFF",
  border: "1px solid #CFE0DE",
  borderRadius: 12,
  padding: "8px 16px",
  marginBottom: 16,
  boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
};
const labelStyle = { fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 };
const sectionLabel = {
  fontSize: 12,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 12,
};

function StandardReports({ requests }) {
  const [shelterRows, setShelterRows] = useState([]);
  const [typeCategories, setTypeCategories] = useState([]);
  const [byType, setByType] = useState(new Map());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [sh, cats] = await Promise.all([listAllShelters(), loadCustomerTypeCategories()]);
        if (!active) return;
        if (sh) setShelterRows(sh);
        if (cats) {
          setTypeCategories(cats.categories);
          setByType(cats.byType);
        }
      } catch (_e) {
        // Degrade to empty data — the report just comes out empty rather than crash.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [fState, setFState] = useState("All");
  const [fCustomerType, setFCustomerType] = useState(""); // "" = All (a customer CATEGORY)
  const [fCategory, setFCategory] = useState("all");
  const [groupBySub, setGroupBySub] = useState(false);
  const [fDecision, setFDecision] = useState("approved");
  const [fReport, setFReport] = useState("shelters");
  const [loadingRequests, setLoadingRequests] = useState(false);

  // shelter_code -> the service's broad customer-type CATEGORY (db-124), the same
  // mapping the CRM Audience "Customer type" filter uses.
  const categoryByCode = useMemo(() => {
    const m = {};
    for (const s of shelterRows) {
      if (s.shelter_code) m[s.shelter_code] = byType.get(s.customer_type || "") || "";
    }
    return m;
  }, [shelterRows, byType]);

  const shownCategoryKeys =
    fCategory === "all" ? STANDARD_CATEGORIES.map((c) => c.key) : [fCategory];

  // shelter_code -> state (for the Active Users report's per-state grouping).
  const stateByCode = useMemo(() => {
    const m = {};
    for (const s of shelterRows) if (s.shelter_code) m[s.shelter_code] = s.state || "";
    return m;
  }, [shelterRows]);

  // Report 3 (Active Users) + Report 5 (Shelters Without an Activated Login) share the
  // same source data — the membership + profile lists + the admin sign-in lookup (EF).
  // Gathered on demand (only when one of those reports is requested); degrades to nulls
  // on any error so the report just comes out empty.
  async function gatherUsersData() {
    try {
      const [memberships, profiles] = await Promise.all([
        listAllUserShelters(),
        listShelterProfiles(),
      ]);
      const deactivatedByUser = {};
      for (const p of profiles || []) if (p?.id) deactivatedByUser[p.id] = !!p.deactivated;
      const activeIds = Array.from(
        new Set(
          (memberships || [])
            .filter((m) => m.user_id && !deactivatedByUser[m.user_id])
            .map((m) => m.user_id)
        )
      );
      const activation = await fetchLoginActivation(activeIds);
      return { memberships: memberships || [], deactivatedByUser, activation };
    } catch (_e) {
      return null;
    }
  }

  async function printPdf() {
    if (loadingRequests) return;
    // Reports 2 + 4 and the Statistics Summary need EVERY request, not the shared list
    // (capped at LIST_LIMIT), so load them in full when printed (12 Oct 2026). If that
    // fails, say so and fall back to the capped list rather than print nothing.
    let allRequests = requests || [];
    if (["children", "requests", "statistics", "all"].includes(fReport)) {
      setLoadingRequests(true);
      const full = await listAllVisibleRequests();
      setLoadingRequests(false);
      if (full) {
        allRequests = full;
      } else {
        await alertDialog({
          title: "Couldn't load every request",
          message: `This PDF will cover only the most recent ${LIST_LIMIT} requests, so totals may be short. Try again for a complete report.`,
        });
      }
    }
    // Report 1 — Number of Shelters (State + dates only; ignores the other filters).
    const shelterReport = shelterCountsByState(shelterRows, fromDate, toDate, { state: fState });
    // Reports 2 + 4 work off the date-filtered requests; the aggregators apply
    // State + Customer-type-category (Report 4 drops drafts + ignores Category).
    const inRange = allRequests.filter((r) => {
      // Brisbane business day (reportDay), not the legacy UTC-baked `date`.
      const rday = r.reportDay || r.date || "";
      if (fromDate && rday < fromDate) return false;
      if (toDate && rday > toDate) return false;
      return true;
    });
    const catReport = childrenByCategory(inRange, {
      decision: fDecision,
      state: fState,
      customerCategory: fCustomerType,
      categoryByCode,
    });
    const requestsReport = requestsByState(inRange, {
      decision: fDecision,
      state: fState,
      customerCategory: fCustomerType,
      categoryByCode,
    });
    // Active Users (Report 3) + Shelters-without-login (Report 5) share one data load.
    let usersReport;
    let noLoginReport;
    if (fReport === "users" || fReport === "no_login" || fReport === "all") {
      const u = await gatherUsersData();
      if (fReport === "users" || fReport === "all") {
        usersReport = u
          ? usersByState({ ...u, stateByCode, shelters: shelterRows, state: fState })
          : {
              rows: [],
              totals: { logins: 0, signedIn: 0, activeShelters: 0, sheltersSignedIn: 0 },
            };
      }
      if (fReport === "no_login" || fReport === "all") {
        noLoginReport = u
          ? sheltersWithoutActivatedLogin({ ...u, shelters: shelterRows, state: fState })
          : { rows: [], total: 0 };
      }
    }

    // Statistics Summary — Approved-only, scoped by From/To/State (mirrors the
    // Statistics dashboard). Response Time needs the responder task list, gathered
    // on demand like the Active-Users report.
    let statisticsReport;
    let respHours;
    if (fReport === "statistics") {
      statisticsReport = buildStatisticsReport(allRequests, {
        fromDate,
        toDate,
        state: fState,
      });
      try {
        const respTasks = await listResponderTasks();
        respHours = averageResponseHours(
          (respTasks || []).filter((t) => {
            if (t.type !== "new_request") return false;
            const d = (t.created_at || "").slice(0, 10);
            if (fromDate && d < fromDate) return false;
            if (toDate && d > toDate) return false;
            return true;
          })
        );
      } catch (_e) {
        respHours = null;
      }
    }

    openStandardReportPdf({
      report: fReport,
      filters: {
        fromDate,
        toDate,
        state: fState,
        customerType: fCustomerType,
        category: fCategory,
        groupBySub,
        decision: fDecision,
      },
      shelterReport,
      catReport,
      shownCategoryKeys,
      usersReport,
      noLoginReport,
      requestsReport,
      statisticsReport,
      respHours,
    });
  }

  return (
    <div style={{ marginTop: 8 }}>
      <h2 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 800, color: "#1A2E2B" }}>
        Standard Reports
      </h2>
      <div style={{ fontSize: 12, color: "#607D79", marginBottom: 14 }}>
        All dates and totals are based on Brisbane (AEST) business days.
      </div>

      {/* FILTER ROW */}
      <div style={cardStyle}>
        <div style={sectionLabel}>Filter</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div style={labelStyle}>FROM DATE</div>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={selectStyle}
            />
          </div>
          <div>
            <div style={labelStyle}>TO DATE</div>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={selectStyle}
            />
          </div>
          <div>
            <div style={labelStyle}>STATE</div>
            <select
              value={fState}
              onChange={(e) => setFState(e.target.value)}
              className={selectStyle}
            >
              {STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>DFV SERVICE TYPE</div>
            <select
              value={fCustomerType}
              onChange={(e) => setFCustomerType(e.target.value)}
              className={selectStyle}
            >
              <option value="">All</option>
              {typeCategories.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>CATEGORY</div>
            <select
              value={fCategory}
              onChange={(e) => setFCategory(e.target.value)}
              className={selectStyle}
            >
              <option value="all">All Items</option>
              {STANDARD_CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>DECISION</div>
            <select
              value={fDecision}
              onChange={(e) => setFDecision(e.target.value)}
              className={selectStyle}
            >
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
              <option value="both">Both</option>
            </select>
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#1A2E2B",
              marginTop: 18,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={groupBySub}
              onChange={(e) => setGroupBySub(e.target.checked)}
            />
            Group / show by sub-category
          </label>
        </div>
      </div>

      {/* REPORT PICKER + PRINT */}
      <div style={cardStyle}>
        <div style={sectionLabel}>Print a report</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={labelStyle}>REPORT</div>
            <select
              value={fReport}
              onChange={(e) => setFReport(e.target.value)}
              className={selectStyle}
              style={{ minWidth: 280 }}
            >
              <option value="shelters">Number of Shelters</option>
              <option value="children">Children Assisted by Category</option>
              <option value="users">Active Users</option>
              <option value="requests">Number of Requests</option>
              <option value="no_login">Shelters Without an Activated Login</option>
              <option value="statistics">Statistics Summary</option>
              <option value="all">All reports</option>
            </select>
          </div>
          <button
            type="button"
            onClick={printPdf}
            disabled={loadingRequests}
            style={{
              background: "#00695C",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 8,
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {loadingRequests ? "Loading all requests…" : "Preview / Print PDF"}
          </button>
        </div>
        <div style={{ fontSize: 12, color: "#607D79", marginTop: 8 }}>
          Opens the report as a PDF in a new tab for preview and printing.
        </div>
      </div>
    </div>
  );
}

export { StandardReports };
